import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, AlertCircle, Navigation, RotateCw, Loader2 } from 'lucide-react';
import { initGoogleMaps, computeRoutes, type RouteAlternative } from '../../lib/googleMaps';
import { ApiError } from '../../lib/api';
import { plural } from '@/lib/plural';

interface GoogleRouteMapProps {
  origin: string;
  destination: string;
  vehicleType: string;
  selectedRouteIndex: number;
  onSelectRouteIndex: (index: number) => void;
  onRoutesCalculated?: (routes: RouteAlternative[]) => void;
  onGoogleApiUnavailable?: (error: string) => void;
  className?: string;
}

// Google Maps–style route lines: darker outline drawn under a lighter fill
const ROUTE_STYLE = {
  selected: {
    outline: { color: '#1557b0', weight: 9, zIndex: 11 },
    fill: { color: '#1a73e8', weight: 6, zIndex: 12 },
  },
  alternative: {
    outline: { color: '#669df6', weight: 8, zIndex: 1 },
    fill: { color: '#aecbfa', weight: 5, zIndex: 2 },
  },
} as const;

// Technical detail shown under the friendly error message, to help diagnose failures
function describeError(err: unknown): string {
  const cause = err instanceof Error ? err.cause : undefined;
  if (cause instanceof ApiError) {
    return `POST /api/v1/mileage/routes → HTTP ${cause.status} ${cause.code}`;
  }
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

export function GoogleRouteMap({
  origin,
  destination,
  vehicleType,
  selectedRouteIndex,
  onSelectRouteIndex,
  onRoutesCalculated,
  onGoogleApiUnavailable,
  className = '',
}: GoogleRouteMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const geometryLibRef = useRef<google.maps.GeometryLibrary | null>(null);
  const markerLibRef = useRef<google.maps.MarkerLibrary | null>(null);

  // Store callbacks in refs to avoid dependency loops
  const onRoutesCalculatedRef = useRef(onRoutesCalculated);
  onRoutesCalculatedRef.current = onRoutesCalculated;

  const onSelectRouteIndexRef = useRef(onSelectRouteIndex);
  onSelectRouteIndexRef.current = onSelectRouteIndex;

  const onGoogleApiUnavailableRef = useRef(onGoogleApiUnavailable);
  onGoogleApiUnavailableRef.current = onGoogleApiUnavailable;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [routes, setRoutes] = useState<RouteAlternative[]>([]);
  const [lastCalculatedQuery, setLastCalculatedQuery] = useState<string>('');

  const trimmedOrigin = origin.trim();
  const trimmedDest = destination.trim();
  const hasBothLocations = Boolean(trimmedOrigin && trimmedDest);
  const addressKey = `${trimmedOrigin.toLowerCase()}|${trimmedDest.toLowerCase()}`;
  const vehicleKey = vehicleType.trim().toLowerCase();
  const currentKey = `${addressKey}|${vehicleKey}`;
  const hasCalculatedForCurrent = Boolean(routes.length > 0 && lastCalculatedQuery === currentKey);
  const hasAddressChanged = Boolean(routes.length > 0 && lastCalculatedQuery !== currentKey);

  // Clear existing polylines & markers from map
  const clearMapOverlays = useCallback(() => {
    polylinesRef.current.forEach((p) => p.setMap(null));
    polylinesRef.current = [];
    markersRef.current.forEach((m) => (m.map = null));
    markersRef.current = [];
  }, []);

  // Reset when either location is cleared
  useEffect(() => {
    if (!hasBothLocations) {
      clearMapOverlays();
      mapInstanceRef.current = null;
      setRoutes([]);
      setLastCalculatedQuery('');
      setError(null);
      onRoutesCalculatedRef.current?.([]);
    }
  }, [hasBothLocations, clearMapOverlays]);

  // Clear error if user changes either address
  const lastPairRef = useRef(currentKey);
  useEffect(() => {
    if (lastPairRef.current !== currentKey) {
      setError(null);
      lastPairRef.current = currentKey;
    }
  }, [currentKey]);

  // Handle explicit route calculation
  const handleCalculateRoute = useCallback(async () => {
    if (!hasBothLocations || loading) return;

    setLoading(true);
    setError(null);
    setErrorDetail(null);

    try {
      // 1. Initialize Google Maps instance if not already ready
      if (!mapInstanceRef.current && mapContainerRef.current) {
        const { Map, geometryLib, markerLib, mapId } = await initGoogleMaps();
        geometryLibRef.current = geometryLib;
        markerLibRef.current = markerLib;
        const map = new Map(mapContainerRef.current, {
          center: { lat: 3.139, lng: 101.6869 },
          zoom: 12,
          mapId,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });
        mapInstanceRef.current = map;
      }

      // 2. Fetch routes via Routes API v2 (or cached / quota fallback)
      const calculatedRoutes = await computeRoutes(trimmedOrigin, trimmedDest, vehicleType);
      setLastCalculatedQuery(currentKey);
      setLoading(false);

      if (calculatedRoutes.length === 0) {
        clearMapOverlays();
        setRoutes([]);
        onRoutesCalculatedRef.current?.([]);
        setError('No driving routes found between these locations.');
        return;
      }

      setRoutes(calculatedRoutes);
      onRoutesCalculatedRef.current?.(calculatedRoutes);
    } catch (err: unknown) {
      setLoading(false);
      clearMapOverlays();
      const msg = err instanceof Error ? err.message : 'Failed to calculate routes.';
      setError(msg);
      setErrorDetail(describeError(err));
      setRoutes([]);
      onRoutesCalculatedRef.current?.([]);
      onGoogleApiUnavailableRef.current?.(msg);
    }
  }, [hasBothLocations, loading, trimmedOrigin, trimmedDest, vehicleType, currentKey, clearMapOverlays]);

  // Vehicle changed on an already-routed trip: refetch so the map shows routes for that vehicle
  const lastVehicleRef = useRef(vehicleKey);
  useEffect(() => {
    const previousVehicle = lastVehicleRef.current;
    if (previousVehicle === vehicleKey) return;
    lastVehicleRef.current = vehicleKey;
    if (routes.length > 0 && lastCalculatedQuery === `${addressKey}|${previousVehicle}`) {
      void handleCalculateRoute();
    }
  }, [vehicleKey]);

  // Render polylines and markers whenever routes or selection change
  useEffect(() => {
    const map = mapInstanceRef.current;
    const geometry = geometryLibRef.current;
    if (!map || routes.length === 0) return;

    clearMapOverlays();
    const bounds = new google.maps.LatLngBounds();
    let hasValidPath = false;

    routes.forEach((r, i) => {
      if (!r.encodedPolyline || !geometry) return;
      try {
        const path = geometry.encoding.decodePath(r.encodedPolyline);
        if (path.length === 0) return;

        hasValidPath = true;
        path.forEach((pt) => bounds.extend(pt));

        const style = i === selectedRouteIndex ? ROUTE_STYLE.selected : ROUTE_STYLE.alternative;
        for (const layer of [style.outline, style.fill]) {
          const polyline = new google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: layer.color,
            strokeOpacity: 1,
            strokeWeight: layer.weight,
            zIndex: layer.zIndex,
            map,
          });

          polyline.addListener('click', () => {
            onSelectRouteIndexRef.current(i);
          });

          polylinesRef.current.push(polyline);
        }
      } catch {
        // polyline decode fallback
      }
    });

    if (hasValidPath) {
      // Add Origin (A) and Destination (B) Markers using first path
      const firstPath = routes[0]?.encodedPolyline && geometry
        ? geometry.encoding.decodePath(routes[0].encodedPolyline)
        : null;

      const markerLib = markerLibRef.current;
      if (firstPath && firstPath.length > 0 && markerLib) {
        // Same convention as Google Maps directions: start is a ringed dot centred on the
        // route, destination is the default red pin
        const startDot = document.createElement('div');
        startDot.className = 'h-3.5 w-3.5 rounded-full border-[3px] border-[#202124] bg-white';
        const startMarker = new markerLib.AdvancedMarkerElement({
          map,
          position: firstPath[0],
          title: `From: ${origin}`,
          content: startDot,
          anchorLeft: '-50%',
          anchorTop: '-50%',
        });
        const endMarker = new markerLib.AdvancedMarkerElement({
          map,
          position: firstPath[firstPath.length - 1],
          title: `To: ${destination}`,
          content: new markerLib.PinElement(),
        });
        markersRef.current.push(startMarker, endMarker);
      }

      map.fitBounds(bounds, 40);
    }
  }, [routes, selectedRouteIndex, origin, destination, clearMapOverlays]);

  // Clean inactive placeholder when both locations are not yet entered
  if (!hasBothLocations) {
    return (
      <div
        className={`flex h-[270px] w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-2/30 p-6 text-center transition-all ${className}`}
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-surface shadow-xs text-text-2">
          <MapPin className="h-5 w-5 text-text-2/70" />
        </div>
        <p className="mt-3 text-[13px] font-semibold text-text">Map Preview Inactive</p>
        <p className="mt-1 max-w-[250px] text-xs text-text-2">
          Enter both <span className="font-medium text-text">From</span> and <span className="font-medium text-text">To</span> locations above to load the route on Google Maps.
        </p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Route Preview Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-[13px] font-semibold text-text">Route Preview</label>
          {routes.length > 0 && hasCalculatedForCurrent && (
            <span className="text-[11px] text-text-2">
              ({plural(routes.length, 'option')})
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleCalculateRoute}
          disabled={!hasBothLocations || loading || Boolean(error)}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors shadow-xs ${
            error
              ? 'bg-surface-2 text-text-2 border border-border cursor-not-allowed opacity-50'
              : hasAddressChanged
              ? 'bg-amber-600 text-white hover:bg-amber-700 animate-pulse'
              : hasCalculatedForCurrent
              ? 'bg-surface-2 text-text hover:bg-surface-2/80 border border-border'
              : 'bg-primary text-white hover:bg-primary-hover'
          } disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Calculating...</span>
            </>
          ) : error ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-warn" />
              <span>Unavailable</span>
            </>
          ) : hasAddressChanged ? (
            <>
              <RotateCw className="h-3.5 w-3.5" />
              <span>Update Route</span>
            </>
          ) : hasCalculatedForCurrent ? (
            <>
              <RotateCw className="h-3.5 w-3.5" />
              <span>Recalculate</span>
            </>
          ) : (
            <>
              <Navigation className="h-3.5 w-3.5" />
              <span>Get Route</span>
            </>
          )}
        </button>
      </div>

      {/* Main Map / Placeholder Box */}
      <div className="relative isolate flex flex-col overflow-hidden rounded-lg border border-border bg-surface-2/20 min-h-[270px]">
        {/* Map View Container */}
        <div
          ref={mapContainerRef}
          className={`h-[270px] w-full bg-surface-2/40 transition-opacity ${error ? 'hidden' : 'block'}`}
          aria-label="Google Maps Route View"
        />

        {/* Error / Quota state */}
        {error && !loading && (
          <div className="flex h-[270px] w-full flex-col items-center justify-center p-6 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-warn/15 text-warn shadow-xs">
              <AlertCircle className="h-5 w-5" />
            </div>
            <p className="mt-3 text-[13px] font-semibold text-text">{error}</p>
            {errorDetail && (
              <p className="mt-1 max-w-full break-all font-mono text-[11px] text-danger">{errorDetail}</p>
            )}
            <p className="mt-1.5 max-w-[320px] text-xs text-text-2">
              Google Maps is unavailable right now. Distance and reimbursement have been automatically calculated using OpenStreetMap.
            </p>
          </div>
        )}

        {/* Not calculated yet prompt (only shown if NO error and NO routes yet) */}
        {!error && routes.length === 0 && !loading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-surface/90 backdrop-blur-xs p-6 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Navigation className="h-5 w-5" />
            </div>
            <p className="mt-3 text-[13px] font-semibold text-text">Ready to Calculate Route</p>
            <p className="mt-1 max-w-[280px] text-xs text-text-2 truncate">
              From <span className="font-medium text-text">{origin}</span> to <span className="font-medium text-text">{destination}</span>
            </p>
            <button
              type="button"
              onClick={handleCalculateRoute}
              className="mt-3.5 inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-1.5 text-xs font-medium text-white shadow-xs hover:bg-primary-hover transition-colors"
            >
              <Navigation className="h-3.5 w-3.5" />
              Get Route & Alternatives
            </button>
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-surface/75 backdrop-blur-[2px]">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="mt-2 text-xs font-medium text-text-2">Calculating routes with Google Maps...</p>
          </div>
        )}

        {/* Floating address changed badge */}
        {!error && hasAddressChanged && !loading && (
          <div className="absolute inset-x-3 top-3 z-20 flex items-center justify-between rounded-md border border-amber-500/40 bg-surface/95 px-3 py-2 text-xs text-text shadow-sm backdrop-blur-sm">
            <span className="text-[11px] font-medium text-text">Address changed since last calculation</span>
            <button
              type="button"
              onClick={handleCalculateRoute}
              className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-0.5 text-2xs font-semibold text-white hover:bg-amber-700"
            >
              Update Route
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
